<template>
    <vue-scroll v-if="order">
        <div class="page-header">
            <el-button
                @click="goBack"
                icon="mdi mdi-arrow-left"
                type="default"
                class="back-button mr-10"
                >Back to Orders
            </el-button>
            <h1>Order Details</h1>
        </div>
        <div style="max-width: 620px">
            <p>
                Current Status:
                {{ order.latest_status.status | capitalize_all }}
            </p>
            <el-card>
                <div class="clearfix" slot="header">
                    <span>Order Breakdown</span>
                </div>
                <breakdown :editable="false" :order="order" />
            </el-card>
            <el-card class="box-card mt-10">
                <div class="clearfix" slot="header">
                    <span>Order Confirmation</span>
                </div>
                <template v-if="isClosed">
                    <span>
                        <el-button
                            @click="viewInvoice()"
                            icon="mdi mdi-eye"
                            round
                            type="primary"
                            v-if="!order.has_sage_invoice"
                            >View Order Confirmation
                        </el-button>
                        <el-button
                            @click="viewSageInvoice()"
                            icon="mdi mdi-eye"
                            round
                            type="primary"
                            v-if="order.has_sage_invoice"
                            >View Invoice
                        </el-button>
                        <el-button
                            @click="getPaymentUrl()"
                            icon="mdi mdi-credit-card"
                            round
                            type="danger"
                            style="padding-left: 10px"
                            v-if="
                                order.has_sage_invoice &&
                                !order.is_paid &&
                                !current_community.is_bill_to_account_only
                            "
                            >Pay Now
                        </el-button>
                    </span>
                </template>
                <template v-else>
                    <el-alert
                        :closable="false"
                        show-icon
                        title="You can view the invoice here once the order is closed."
                        type="info"
                    >
                    </el-alert>
                </template>
            </el-card>
            <el-card
                class="box-card mt-10"
                v-if="
                    !$store.state.config.settings.disable_order_download &&
                    (can(Permissions.CREATE_CALENDAR) ||
                        can(Permissions.CREATE_NEWSLETTER))
                "
            >
                <div class="clearfix" slot="header">
                    <span>Downloads</span
                    ><span v-if="isApproved" class="float-right">
                        <el-button
                            :loading="downloadCalendarsForm.busy"
                            @click="downloadCalendars()"
                            icon="mdi mdi-download"
                            type="text"
                            >Download All
                        </el-button>
                    </span>
                </div>
                <el-alert
                    :closable="false"
                    show-icon
                    title="Your order needs to be approved before you can download the calendar files."
                    type="info"
                    v-if="!isApproved"
                >
                </el-alert>
                <div v-if="isApproved">
                    <el-collapse>
                        <el-collapse-item
                            v-for="(
                                calendar, calendar_index
                            ) in calendarItemsForDownload"
                            :name="calendar.calendar_name"
                            :title="calendar.calendar_name + ' Calendar'"
                            :key="calendar_index"
                        >
                            <div
                                v-for="(
                                    download, download_index
                                ) in calendar.downloads"
                                :key="download_index"
                            >
                                <img
                                    :src="fileThumbnail(download)"
                                    class="mb-10 align-center"
                                    width="100"
                                    style="border: 1px solid"
                                />
                                <el-button
                                    :key="download_index"
                                    :loading="downloadCalendarItemForm.busy"
                                    @click="downloadOrderItem(download)"
                                    type="primary"
                                    round
                                    icon="mdi mdi-download"
                                    class="mb-40 align-center"
                                    >Download
                                    {{ calendar.calendar_name }} Calendar
                                    {{ download.file_format }} ({{
                                        download.proof ? "Proof" : "Final"
                                    }})
                                </el-button>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>
            </el-card>
        </div>
    </vue-scroll>
</template>

<script>
import * as ApprovalStatus from "@/constants/approval-status";
import * as BusEvents from "@/constants/bus-events";
import * as OrderStatuses from "@/constants/order-statuses";
import * as OrderableTypes from "@/constants/orderable-types";
import auth_mixin from "@/mixins/auth";
import order_status_mixin from "@/mixins/order-status";
import FileUtils from "@/utils/file";
import OrderUtils from "@/utils/order";
import Breakdown from "@/views/pages/order/Breakdown";
import { mapState } from "vuex";

function getData(id) {
    return window.axios.get(`/web-api/order/${id}`, {
        params: {
            include:
                "items,statuses.updater,community.locations,latest_status,items.orderable,items.location,items.sizes.size"
        }
    });
}

export default {
    mixins: [order_status_mixin, auth_mixin],
    components: {
        Breakdown
    },
    beforeRouteEnter(to, from, next) {
        getData(to.params.id).then((response) => {
            next((vm) => vm.setData(response));
        });
    },
    beforeRouteUpdate(to, from, next) {
        getData(to.params.id).then((response) => {
            this.setData(response);
            next();
        });
    },
    data() {
        return {
            OrderStatuses,
            ApprovalStatus,
            OrderableTypes,
            order: null,
            statuses: null,
            approvalForm: new window.Form({
                approval_status: null
            }),
            duplicateForm: new window.Form({}),
            logs: {
                data: [],
                meta: {
                    pagination: null
                }
            },
            rush_processing: false,
            trackingForm: new window.Form({
                tracking_number: null
            }),
            downloadCalendarsForm: new window.Form(),
            downloadCalendarItemForm: new window.Form(),
            // calendarItemsForDownload = [{"calendar_name":"Calendar","downloads":[{"calendar_order_item_id":29892,"file_format":"PDF","proof":0},{"calendar_order_item_id":29892,"file_format":"PDF","proof":1}]}]
            calendarItemsForDownload: []
        };
    },
    computed: {
        ...mapState({
            workflow: (state) => state.config.settings.workflow,
            current_community: (state) => state.current_community
        }),
        isApproved() {
            return this.order && OrderUtils.isApproved(this.order);
        },
        isClosed() {
            return this.order && OrderUtils.isClosed(this.order);
        },
        calendarItems() {
            return this.order.items.data.filter(
                (item) => item.orderable_type === OrderableTypes.CALENDAR
            );
        },
        newsletterItems() {
            return this.order.items.data.filter(
                (item) => item.orderable_type === OrderableTypes.NEWSLETTER
            );
        }
    },
    mounted() {
        this.getCalendarItemsForDownload();
    },
    methods: {
        setData(response) {
            this.order = response.data;
            this.trackingForm.tracking_number = this.order.tracking_number;
            this.rush_processing = this.order.rush_processing;
        },
        approval(approve = true) {
            this.approvalForm.approval_status = approve
                ? ApprovalStatus.APPROVED
                : ApprovalStatus.REJECTED;
            this.$confirm(
                `Confirm ${approve ? "approve" : "reject"} order?`,
                "Confirmation",
                {
                    confirmButtonText: approve ? "Approve" : "Reject",
                    cancelButtonText: "Cancel",
                    confirmButtonClass: approve
                        ? "el-button--success"
                        : "el-button--danger",
                    type: "info"
                }
            )
                .then(() => {
                    window.App.put(
                        `/web-api/order/${this.order.id}`,
                        this.approvalForm
                    ).then(({ data }) => {
                        this.$notify.success({
                            message: `Order has been ${
                                approve ? "approved" : "rejected"
                            }.`
                        });
                        _.extend(this.order, data);
                        this.$refs.editStatus.reload();
                        if (!approve) {
                            window.Bus.$emit(
                                BusEvents.ORDER_REJECTED,
                                this.order
                            );
                        }
                    });
                })
                .catch(() => {
                    //user cancelled
                });
        },
        formatOrderSize(order_size) {
            return _.trim(
                `${order_size.quantity} - ${
                    order_size.size.paper_color && order_size.paper_color
                        ? order_size.paper_color
                        : ""
                } ${order_size.size.name}`
            );
        },
        downloadCalendars() {
            this.downloadCalendarsForm.startProcessing();
            window.axios
                .get(`/web-api/order/${this.order.id}/download-calendars`, {
                    responseType: "blob"
                })
                .then((response) => {
                    FileUtils.downloadFileFromBlobResponse(response);
                })
                .finally(() => {
                    this.downloadCalendarsForm.finishProcessing();
                });
        },
        viewInvoice() {
            OrderUtils.openInvoicePdf(this.order);
        },
        viewSageInvoice() {
            OrderUtils.openSageInvoicePdf(this.order);
        },
        downloadOrderItem(orderItem) {
            this.downloadCalendarItemForm.startProcessing();
            window.axios
                .get(`/web-api/order/${this.order.id}/download-order-item`, {
                    responseType: "blob",
                    params: {
                        order_item_id: orderItem.calendar_order_item_id,
                        file_format: orderItem.file_format,
                        proof: orderItem.proof
                    }
                })
                .then((response) => {
                    FileUtils.downloadFileFromBlobResponse(response);
                })
                .finally(() => {
                    this.downloadCalendarItemForm.finishProcessing();
                });
        },
        getCalendarItemsForDownload() {
            window.axios
                .get(
                    `/web-api/order/${this.order.id}/calendar-items-for-download`
                )
                .then((response) => {
                    this.calendarItemsForDownload = response.data;
                });
        },
        fileThumbnail(item) {
            return item.file_format === "PDF"
                ? item.proof
                    ? "/images/order/digital-files/calendar-proof.png"
                    : "/images/order/digital-files/pdf-for-inhouse-printing-or-email.jpeg"
                : "/images/order/digital-files/jpg-for-social-media.png";
        },
        async getPaymentUrl() {
            try {
                const response = await fetch(`/web-api/payment-url`);
                const data = await response.json();
                const paymentUrl = data.payment_url;
                window.open(`${paymentUrl}`, "_blank");
            } catch (error) {
                console.error("Error copying payment URL:", error);
            }
        },
        goBack() {
            // Navigate back to the Orders page
            this.$router.push({ name: "Orders" });
        }
    }
};
</script>

<style lang="css" scoped>
.align-center {
    display: block;
    margin-left: auto;
    margin-right: auto;
}
.back-button {
    margin-right: 10px;
    vertical-align: middle;
}
</style>
<style lang="scss">
@import "../../../../../assets/scss/variables";
</style>
